import React, { useState } from 'react';
import { Button } from "@shared/ui/button";
import { DynamicForm } from "@shared/components/DynamicForm";

import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ArrowRight, CheckCircle2, User, Mail, MapPin, Home, Settings, Calendar, ShieldCheck, Loader2 } from "lucide-react";
import { useParams, useNavigate } from 'react-router-dom';
import { useUtilityInfo, useConsumerDetail, useGenerateOtp } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { useToast } from "@shared/hooks/use-toast";

export const VerifyAccount = ({ onNext }: any) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const fields: FormField[] = [
    { 
      name: 'consumerNumber', 
      label: t("Account Number"), 
      type: 'text', 
      required: true, 
      placeholder: t("e.g. 123456789"),
      defaultValue: '',
    }
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    validateOnMount: false,
  });
  const [verified, setVerified] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  
  const { tenant, utilityName } = useParams<{ tenant?: string; utilityName?: string }>();
  
  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenant || '', utility_alias: utilityName || '' },
    { enabled: !!tenant && !!utilityName }
  );

  const consumerNo = form.watch('consumerNumber');

  const { refetch, isFetching, data: consumerDetailData } = useConsumerDetail(
    { 
      remote_utility_id: utilityInfo?.result?.id, 
      consumer_no: consumerNo 
    },
    { enabled: false }
  );

  const generateOtpMutation = useGenerateOtp();

  const handleVerifySubmit = async (data: any) => {
    if (data.consumerNumber) {
      setConfirmed(false);
      setVerified(false);
      const result = await refetch();
      if (result.isSuccess && result.data) {
        setVerified(true);
      } else {
        console.error("Verification failed", result.error);
        const errorData = result.error as any;
        const errorMessage = errorData?.message || errorData?.response?.data?.message || t("Verification failed. Please try again.");
        toast({
          title: t("Error"),
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleContinueSubmit = () => {
    if (confirmed) {
      const email = consumerDetailData?.result?.email;
      if (!email) {
        console.error("No email found in consumer details");
        return;
      }

      const payload = {
        email,
        remote_utility_id: utilityInfo?.result?.id
      };

      generateOtpMutation.mutate(payload, {
        onSuccess: (res) => {
          sessionStorage.setItem('forgot_password_email', email);
          const secretKey = 
            res?.otp?.result?.secretKey || 
            res?.result?.otp?.result?.secretKey || 
            res?.data?.otp?.result?.secretKey || 
            res?.secretKey;
            
          if (secretKey) {
            sessionStorage.setItem('forgot_password_secret_key', secretKey);
          } else {
            console.error("Could not find secret_key in response:", res);
          }
          if (consumerNo) {
            sessionStorage.setItem('create_account_consumer_no', consumerNo);
          }
          if (consumerDetailData?.result) {
            const res = consumerDetailData.result;
            sessionStorage.setItem('create_account_first_name', res.firstName || '');
            sessionStorage.setItem('create_account_last_name', res.lastName || '');
            sessionStorage.setItem('create_account_type', res.ownershipDisplay || res.categoryDisplay || res.category || '');
            sessionStorage.setItem('create_account_email', res.email || '');
            const address = res.addressData?.service?.ADDRESS || res.addressData?.service?.address || res.territoryData?.service?.address || '';
            sessionStorage.setItem('create_account_address', address);
            sessionStorage.setItem('create_account_services', JSON.stringify(res.utilityService || []));
          }
          if (utilityInfo?.result?.id) {
            sessionStorage.setItem('create_account_remote_utility_id', String(utilityInfo.result.id));
          }
          onNext();
        },
        onError: (err) => {
          console.error("Failed to generate OTP:", err);
        }
      });
    }
  };

  const renderFooter = () => (
    <Button 
      type="button" 
      onClick={() => {
        form.handleSubmit(handleVerifySubmit)();
      }}
      className="w-full mt-2"
      disabled={isFetching}
    >
      {isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
      {t("Verify Account Number")}
    </Button>
  );

  return (
    <div className="flex flex-col p-2">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("Create your account")}</h2>
      <p className="text-gray-500 text-sm mb-6">{t("Enter your Account Number to get started. It's printed on your utility bill.")}</p>
      
      <DynamicForm 
        fields={fields} 
        form={form} 
        onSubmit={() => {}}
        onFieldChange={(fieldName) => {
          if (FormService.getFieldError(form, fieldName)) {
            form.clearErrors(fieldName);
          }
        }}
        config={{ classes: FIELD_CLASSES, showSubmitButton: false }}
        renderCustomFooter={renderFooter}
      />

      {verified && (
        <div className="mt-6">
          <div className="bg-green-50 border border-green-300 rounded-xl overflow-hidden">
            <div className="bg-green-100 px-4 py-3 border-b border-green-200 flex items-center font-bold text-green-800 text-sm">
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" /> {t("Consumer Account Found")}
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-start">
                <User className="h-4 w-4 mr-3 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Name")}</div>
                  <div className="font-medium text-gray-900 text-sm">
                    {consumerDetailData?.result?.firstName} {consumerDetailData?.result?.lastName ? `${consumerDetailData.result.lastName.charAt(0)}*****` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <Mail className="h-4 w-4 mr-3 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Email")}</div>
                  <div className="font-medium text-gray-900 text-sm">
                    {consumerDetailData?.result?.email ? `${consumerDetailData.result.email.substring(0, 2)}***@${consumerDetailData.result.email.split('@')[1]}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="h-4 w-4 mr-3 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Address")}</div>
                  <div className="font-medium text-gray-900 text-sm">
                    {consumerDetailData?.result?.addressData?.service?.ADDRESS || consumerDetailData?.result?.addressData?.service?.address || consumerDetailData?.result?.territoryData?.service?.address || ''}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <Home className="h-4 w-4 mr-3 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Account Type")}</div>
                  <div className="font-medium text-gray-900 text-sm">{consumerDetailData?.result?.ownershipDisplay || consumerDetailData?.result?.categoryDisplay || consumerDetailData?.result?.category || 'NA'}</div>
                </div>
              </div>
              <div className="flex items-start">
                <Settings className="h-4 w-4 mr-3 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Services")}</div>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {consumerDetailData?.result?.utilityService?.map((service: string, idx: number) => (
                      <span key={idx} className="bg-blue-100 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-medium border border-blue-200">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <Calendar className="h-4 w-4 mr-3 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold text-gray-400  tracking-wider">{t("Connected Since")}</div>
                  <div className="font-medium text-gray-900 text-sm">
                    {consumerDetailData?.result?.registrationDate ? new Date(consumerDetailData.result.registrationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-green-200 rounded-lg p-3 flex items-start mt-4">
                <ShieldCheck className="h-4 w-4 mr-2 text-green-600 mt-0.5 shrink-0" />
                <div className="text-[11px] text-gray-500 leading-tight">
                  {t("Personal details are partially masked for security. Please confirm this is your account before continuing.")}
                </div>
              </div>
            </div>
          </div>
          
          {consumerDetailData?.result?.isRegisteredCss ? (
            <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex flex-col items-center justify-center text-center">
              <div className="text-orange-800 font-medium mb-2">
                {t("This account is already registered.")}
              </div>
              <p className="text-sm text-orange-600 mb-4">
                {t("You can directly sign in to access your utility dashboard.")}
              </p>
              <Button 
                onClick={() => navigate(`/${tenant}/${utilityName}/sign-in`)}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {t("Go to Sign In")}
              </Button>
            </div>
          ) : (
            <>
              <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl mt-6 cursor-pointer hover:bg-gray-50 transition-colors">
                <input 
                  type="checkbox" 
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 w-4 h-4 text-blue-600 focus:ring-blue-500" 
                />
                <span className="text-sm text-gray-600 font-medium">{t("I confirm this is my utility account and I am authorized to create an online account for it.")}</span>
              </label>
              
              <Button 
                onClick={handleContinueSubmit} 
                disabled={!confirmed || generateOtpMutation.isPending}
                className={`w-full mt-6 `}
              >
                {generateOtpMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("Continue to Email Verification")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
